import { useState, useEffect, useMemo } from "react";
import {
  FaCheck,
  FaTimes,
  FaEye,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaCreditCard,
  FaUser,
  FaFilter,
  FaSearch,
  FaDownload,
  FaSync,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle
} from "react-icons/fa";
import { useCashbacks } from "../contexts/CashbacksContext";

const Cashbacks = () => {
    const { setPendingCount } = useCashbacks();
  const [cashbacks, setCashbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCashback, setSelectedCashback] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState("all"); // all, requested, paid, rejected
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("date"); // date, amount, status
  const [sortOrder, setSortOrder] = useState("desc"); // asc, desc
  const [referralDeletionStatus, setReferralDeletionStatus] = useState(null); // null, 'success', 'warning', 'error'
  const [referralDeletionMessage, setReferralDeletionMessage] = useState("");
  const [isDeletingReferrals, setIsDeletingReferrals] = useState(false);

  const fetchCashbacks = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors

      const response = await fetch("https://us-central1-workexpress-1ffff.cloudfunctions.net/app/cashback");

      // Handle 404 specifically - cashback collection doesn't exist yet
      if (response.status === 404) {
        setCashbacks([]);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch cashbacks: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      // Fetch servicemen data to get user names
      let servicemenData = [];
      try {
        const servicemenResponse = await fetch("https://us-central1-workexpress-1ffff.cloudfunctions.net/app/servicemen");
        if (servicemenResponse.ok) {
          const servicemenResult = await servicemenResponse.json();
          // The servicemen API returns the array directly, not wrapped in a property
          servicemenData = Array.isArray(servicemenResult) ? servicemenResult : [];
        }
      } catch (servicemenErr) {
        // Continue without servicemen data - users will show as "Unknown User"
      }

      // Map cashback data with user names from servicemen collection
      const cashbacksArray = data.cashbacks || [];
      
      const enrichedCashbacks = cashbacksArray.map(cashback => {
        // Use authId to find the serviceman by uid field (as shown in Firestore)
        const serviceman = servicemenData.find(s => s.uid === cashback.authId);

        const enriched = {
          ...cashback,
          userName: serviceman ? serviceman.name : "Unknown User",
          userId: cashback.authId,
          amountRequested: cashback.amount,
          date: cashback.createdAt
        };
        return enriched;
      });

      setCashbacks(enrichedCashbacks);
      
      // Calculate and update pending cashback withdrawals count
      const pendingCount = enrichedCashbacks.filter(c => 
        c.status === "requested"
      ).length;
      setPendingCount(pendingCount);
    } catch (err) {
      console.error("[Cashbacks] Error fetching cashbacks:", err);
      setError(err.message);
      setPendingCount(0); // Reset count on error
    } finally {
      setLoading(false);
    }
  };

  const deleteMatchingReferrals = async (referrerAuthId, approvalTimestamp) => {
    try {
      setIsDeletingReferrals(true);

      // First, fetch all referrals to find matching ones
      const referralsResponse = await fetch("https://us-central1-workexpress-1ffff.cloudfunctions.net/app/referrals");

      if (!referralsResponse.ok) {
        throw new Error(`Failed to fetch referrals: ${referralsResponse.status} ${referralsResponse.statusText}`);
      }

      const referralsData = await referralsResponse.json();
      const referrals = Array.isArray(referralsData) ? referralsData : [referralsData];


      // Filter referrals that match the criteria
      const approvalDate = new Date(approvalTimestamp);
      const matchingReferrals = referrals.filter(referral => {
        // Check if referrerAuthId matches
        const authIdMatch = referral.referrerAuthId === referrerAuthId;

        // Check if createdAt is before or equal to approval timestamp
        let dateMatch = false;
        if (referral.createdAt) {
          let referralDate;

          // Handle different date formats
          if (referral.createdAt._seconds) {
            // Firebase Timestamp
            referralDate = new Date(referral.createdAt._seconds * 1000);
          } else if (typeof referral.createdAt === 'string') {
            referralDate = new Date(referral.createdAt);
          } else if (referral.createdAt instanceof Date) {
            referralDate = referral.createdAt;
          }

          if (referralDate) {
            dateMatch = referralDate <= approvalDate;
          }
        }

        // Check if status is approved or declined (NOT pending)
        const statusMatch = referral.status === "approved" || referral.status === "declined";

        return authIdMatch && dateMatch && statusMatch;
      });


      if (matchingReferrals.length === 0) {
        return { deletedCount: 0 };
      }

      // Delete each matching referral using individual delete endpoints
      let deletedCount = 0;

      const deletePromises = matchingReferrals.map(async (referral) => {
        try {

          const deleteResponse = await fetch(`https://us-central1-workexpress-1ffff.cloudfunctions.net/app/referrals/${referral.id}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (deleteResponse.ok) {
            deletedCount++;
            return { success: true, id: referral.id };
          } else {
            console.error('Failed to delete referral:', referral.id, deleteResponse.status);
            return { success: false, id: referral.id };
          }
        } catch (err) {
          console.error('Failed to delete referral:', referral.id, err);
          return { success: false, id: referral.id };
        }
      });

      await Promise.all(deletePromises);

      return { deletedCount };

    } catch (err) {
      console.error("Error processing matching referrals:", err);
      throw err;
    } finally {
      setIsDeletingReferrals(false);
    }
  };

  const updateCashbackStatus = async (cashbackId, newStatus) => {
    try {
      // Get the current cashback record to access referrerAuthId
      const currentCashback = cashbacks.find(cb => cb.id === cashbackId);
      if (!currentCashback) {
        throw new Error("Cashback record not found");
      }

      const approvalTimestamp = new Date().toISOString();

      const response = await fetch(`https://us-central1-workexpress-1ffff.cloudfunctions.net/app/cashback/${cashbackId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: newStatus,
          approvalTimestamp: approvalTimestamp,
          referrerAuthId: currentCashback.authId // Include the referrerAuthId for backend processing
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update cashback status");
      }

      // If marking as paid, also delete matching referral records
      if (newStatus === "paid" && currentCashback.authId) {
        try {
          await deleteMatchingReferrals(currentCashback.authId, approvalTimestamp);
        } catch (referralErr) {
          console.warn("Failed to delete matching referrals:", referralErr);
          // Silently continue - don't show any error messages to user
        }
      }

      // Refresh the cashbacks list
      await fetchCashbacks();
      setShowModal(false);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchCashbacks();
  }, []);

  const filteredAndSortedCashbacks = useMemo(() => {
    let filtered = cashbacks.filter(cashback => {
      // Status filter
      if (filter !== "all" && cashback.status !== filter) return false;

      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          (cashback.userName && cashback.userName.toLowerCase().includes(searchLower)) ||
          (cashback.userId && cashback.userId.toLowerCase().includes(searchLower)) ||
          (cashback.paymentMethod && cashback.paymentMethod.toLowerCase().includes(searchLower)) ||
          (cashback.monthKey && cashback.monthKey.toLowerCase().includes(searchLower))
        );
      }

      return true;
    });

    // Sort the filtered results
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "amount":
          aValue = parseFloat(a.amountRequested) || 0;
          bValue = parseFloat(b.amountRequested) || 0;
          break;
        case "status":
          aValue = a.status;
          bValue = b.status;
          break;
        case "date":
        default:
          aValue = new Date(a.date || 0);
          bValue = new Date(b.date || 0);
          break;
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [cashbacks, filter, searchTerm, sortBy, sortOrder]);

  const getStatusColor = (status) => {
    switch (status) {
      case "requested": return "bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 border-amber-200";
      case "paid": return "bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-800 border-emerald-200";
      case "rejected": return "bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border-red-200";
      default: return "bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "requested": return <FaClock className="w-4 h-4" />;
      case "paid": return <FaCheckCircle className="w-4 h-4" />;
      case "rejected": return <FaTimesCircle className="w-4 h-4" />;
      default: return <FaExclamationTriangle className="w-4 h-4" />;
    }
  };

  const formatDate = (dateInput) => {
    if (!dateInput) return "N/A";

    try {
      let date;

      // Handle Firebase Timestamp object: {_seconds: 1759837276, _nanoseconds: 262000000}
      if (dateInput && typeof dateInput === 'object' && dateInput._seconds) {
        // Convert Firebase Timestamp to JavaScript Date
        date = new Date(dateInput._seconds * 1000);
      }
      // Handle the specific string format: "7 October 2025 at 17:11:16 UTC+5:30"
      else if (typeof dateInput === 'string' && dateInput.includes('at')) {
        // Parse the custom format: "7 October 2025 at 17:11:16 UTC+5:30"
        const parts = dateInput.split(' at ');
        if (parts.length === 2) {
          const datePart = parts[0]; // "7 October 2025"
          const timePart = parts[1].split(' UTC')[0]; // "17:11:16"

          // Create a proper date string
          const fullDateTime = `${datePart} ${timePart}`;
          date = new Date(fullDateTime);

          // If that doesn't work, try a different approach
          if (isNaN(date.getTime())) {
            // Try parsing with different format
            const monthMap = {
              'January': '01', 'February': '02', 'March': '03', 'April': '04',
              'May': '05', 'June': '06', 'July': '07', 'August': '08',
              'September': '09', 'October': '10', 'November': '11', 'December': '12'
            };

            const dateMatch = datePart.match(/(\d+)\s+(\w+)\s+(\d+)/);
            if (dateMatch) {
              const day = dateMatch[1].padStart(2, '0');
              const month = monthMap[dateMatch[2]];
              const year = dateMatch[3];
              const isoDate = `${year}-${month}-${day}T${timePart}`;
              date = new Date(isoDate);
            }
          }
        }
      } else {
        // Handle other formats (ISO strings, etc.)
        date = new Date(dateInput);
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return "Invalid Date";
      }

      // Format in AM/PM format
      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      };

      const formattedDate = date.toLocaleDateString('en-US', options);
      return formattedDate;
    } catch (error) {
      return "Invalid Date";
    }
  };

  const formatAmount = (amount) => {
    return `₹${amount || 0}`;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        width: '100%'
      }}>
        <span className="loader"></span>
        <style>{`
          .loader {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            display: inline-block;
            position: relative;
            border: 2px solid #FF3D00;
            box-sizing: border-box;
            animation: rotation 1s linear infinite;
          }
          .loader::after {
            content: '';  
            box-sizing: border-box;
            position: absolute;
            left: 4px;
            top: 4px;
            border: 2px solid #000;
            width: 12px;
            height: 12px;
            border-radius: 50%;
          }
          [data-theme="dark"] .loader::after {
            border: 2px solid #FFF;
          }
          @keyframes rotation {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">Error: {error}</div>
          <button
            onClick={fetchCashbacks}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={{
        minHeight: '100vh',
        background: 'var(--bg-primary, #f8f9fd)',
        padding: '20px'
      }}>
        {/* Full Screen Loader for Referral Deletion */}
        {isDeletingReferrals && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}>
            <div style={{
              background: 'var(--bg-card, #ffffff)',
              padding: '40px',
              borderRadius: '15px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              textAlign: 'center',
              minWidth: '300px'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #2f80ed',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px auto'
              }}></div>
            </div>
          </div>
        )}
        {/* Header Section */}
        <div style={{ marginBottom: '30px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '20px'
          }}>
            <div>
              <h1 style={{
                fontSize: '2.5rem',
                fontWeight: 'bold',
                color: '#3b82f6',
                marginBottom: '10px',
                margin: 0
              }}>
                Cashback Management
              </h1>
              <p style={{
                color: 'var(--text-secondary, #666)',
                fontSize: '1.1rem',
                margin: 0
              }}>
                {cashbacks.length === 0 && !loading && !error && (
                  <span style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    color: '#4caf50',
                    marginTop: '5px',
                    fontWeight: '500'
                  }}>
                  </span>
                )}
              </p>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '15px',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={fetchCashbacks}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 20px',
                  background: 'var(--bg-card, #ffffff)',
                  border: '2px solid var(--border-color, #e0e0e0)',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                <FaSync style={{ marginRight: '8px', fontSize: '16px' }} />
                Refresh
              </button>
            </div>
          </div>
        </div>


        {/* Stats Cards - Only show when there are cashbacks */}
        {cashbacks.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '20px',
            marginBottom: '30px'
          }}>
            {[
              {
                key: "all",
                label: "Total Requests",
                count: cashbacks.length,
                icon: FaMoneyBillWave,
                bgColor: '#e3f2fd',
                iconColor: '#2196f3'
              },
              {
                key: "requested",
                label: "Pending",
                count: cashbacks.filter(c => c.status === "requested").length,
                icon: FaClock,
                bgColor: '#fff3e0',
                iconColor: '#ff9800'
              },
              {
                key: "paid",
                label: "Paid",
                count: cashbacks.filter(c => c.status === "paid").length,
                icon: FaCheckCircle,
                bgColor: '#e8f5e8',
                iconColor: '#4caf50'
              },
              {
                key: "rejected",
                label: "Rejected",
                count: cashbacks.filter(c => c.status === "rejected").length,
                icon: FaTimesCircle,
                bgColor: '#ffebee',
                iconColor: '#f44336'
              },
            ].map((stat) => (
              <div key={stat.key} style={{
                background: stat.bgColor,
                padding: '25px',
                borderRadius: '15px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div>
                    <p style={{
                      color: 'var(--text-secondary, #666)',
                      fontSize: '14px',
                      fontWeight: '500',
                      margin: '0 0 8px 0'
                    }}>
                      {stat.label}
                    </p>
                    <p style={{
                      fontSize: '2.5rem',
                      fontWeight: 'bold',
                      color: 'var(--text-primary, #333)',
                      margin: 0
                    }}>
                      {stat.count}
                    </p>
                  </div>
                  <div style={{
                    padding: '15px',
                    borderRadius: '12px',
                    background: stat.iconColor,
                    color: 'white'
                  }}>
                    <stat.icon style={{ fontSize: '24px' }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Search and Filter Section - Only show when there are cashbacks */}
        {cashbacks.length > 0 && (
          <div style={{
            background: 'var(--bg-card, #ffffff)',
            borderRadius: '15px',
            boxShadow: '0 8px 25px var(--shadow, rgba(0,0,0,0.1))',
            border: '1px solid var(--border-color, #e0e0e0)',
            padding: '25px',
            marginBottom: '25px'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: window.innerWidth < 768 ? 'column' : 'row',
              gap: '20px',
              alignItems: 'center'
            }}>
              {/* Search Bar */}
              <div style={{ flex: 1, position: 'relative', width: '100%' }}>
                <FaSearch style={{
                  position: 'absolute',
                  left: '15px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted, #999)',
                  fontSize: '16px'
                }} />
                <input
                  type="text"
                  placeholder="Search by user name, ID, payment method..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    paddingLeft: '45px',
                    paddingRight: '15px',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    border: '2px solid var(--border-color, #e0e0e0)',
                    borderRadius: '10px',
                    fontSize: '14px',
                    transition: 'all 0.3s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2f80ed';
                    e.target.style.boxShadow = '0 0 0 3px rgba(47, 128, 237, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e0e0e0';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Sort Dropdown */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  style={{
                    padding: '12px 15px',
                    border: '2px solid var(--border-color, #e0e0e0)',
                    borderRadius: '10px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    outline: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2f80ed';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e0e0e0';
                  }}
                >
                  <option value="date">Sort by Date</option>
                  <option value="amount">Sort by Amount</option>
                  <option value="status">Sort by Status</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                  style={{
                    padding: '12px 15px',
                    border: '2px solid var(--border-color, #e0e0e0)',
                    borderRadius: '10px',
                    background: 'var(--bg-card, #ffffff)',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  {sortOrder === "asc" ? "↑" : "↓"}
                </button>
              </div>
            </div>

            {/* Filter Tabs */}
            <div style={{ marginTop: '20px' }}>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px'
              }}>
                {[
                  { key: "all", label: "All Requests", count: cashbacks.length },
                  { key: "requested", label: "Pending", count: cashbacks.filter(c => c.status === "requested").length },
                  { key: "paid", label: "Paid", count: cashbacks.filter(c => c.status === "paid").length },
                  { key: "rejected", label: "Rejected", count: cashbacks.filter(c => c.status === "rejected").length },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setFilter(tab.key)}
                    style={{
                      padding: '12px 20px',
                      borderRadius: '10px',
                      fontSize: '14px',
                      fontWeight: '500',
                      border: 'none',
                      cursor: 'pointer',
                      background: filter === tab.key ? '#3b82f6' : 'var(--bg-tertiary, #f5f5f5)',
                      color: filter === tab.key ? 'white' : 'var(--text-secondary, #666)'
                    }}
                  >
                    {tab.label} ({tab.count})
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {cashbacks.length > 0 && (
          <div style={{
            background: 'var(--bg-card, #ffffff)',
            borderRadius: '15px',
            boxShadow: '0 8px 25px var(--shadow, rgba(0,0,0,0.1))',
            border: '1px solid var(--border-color, #e0e0e0)',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '20px 25px',
              background: 'var(--bg-primary, #f8f9fd)',
              borderBottom: '1px solid #e0e0e0'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: 'var(--text-primary, #333)',
                margin: '0 0 5px 0'
              }}>
                Cashback Requests
              </h3>
              <p style={{
                fontSize: '14px',
                color: 'var(--text-secondary, #666)',
                margin: 0
              }}>
                Showing {filteredAndSortedCashbacks.length} of {cashbacks.length} requests
              </p>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                minWidth: '800px'
              }}>
                <thead style={{
                  background: '#e3f2fd'
                }}>
                  <tr>
                    <th style={{
                      padding: '15px 20px',
                      textAlign: 'left',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: 'var(--text-primary, #333)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderBottom: '2px solid var(--border-color, #e0e0e0)'
                    }}>
                      User Details
                    </th>
                    <th style={{
                      padding: '15px 20px',
                      textAlign: 'left',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: 'var(--text-primary, #333)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderBottom: '2px solid var(--border-color, #e0e0e0)'
                    }}>
                      Amount
                    </th>
                    <th style={{
                      padding: '15px 20px',
                      textAlign: 'left',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: 'var(--text-primary, #333)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderBottom: '2px solid var(--border-color, #e0e0e0)'
                    }}>
                      Payment Method
                    </th>
                    <th style={{
                      padding: '15px 20px',
                      textAlign: 'left',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: 'var(--text-primary, #333)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderBottom: '2px solid var(--border-color, #e0e0e0)'
                    }}>
                      Status
                    </th>
                    <th style={{
                      padding: '15px 20px',
                      textAlign: 'left',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: 'var(--text-primary, #333)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderBottom: '2px solid var(--border-color, #e0e0e0)'
                    }}>
                      Date Requested
                    </th>
                    <th style={{
                      padding: '15px 20px',
                      textAlign: 'left',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: 'var(--text-primary, #333)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderBottom: '2px solid var(--border-color, #e0e0e0)'
                    }}>
                      Month
                    </th>
                    <th style={{
                      padding: '15px 20px',
                      textAlign: 'left',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: 'var(--text-primary, #333)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderBottom: '2px solid var(--border-color, #e0e0e0)'
                    }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedCashbacks.map((cashback, index) => (
                    <tr key={cashback.id} style={{
                      background: index % 2 === 0 ? 'var(--bg-card, #ffffff)' : 'var(--bg-tertiary, #fafafa)',
                      borderBottom: '1px solid var(--border-color, #f0f0f0)'
                    }}>
                      <td style={{ padding: '20px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <div style={{
                            flexShrink: 0,
                            width: '48px',
                            height: '48px',
                            marginRight: '15px'
                          }}>
                            <div style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '50%',
                              background: '#e3f2fd',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}>
                              <FaUser style={{ fontSize: '20px', color: '#2196f3' }} />
                            </div>
                          </div>
                          <div>
                            <div style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              color: 'var(--text-primary, #333)',
                              marginBottom: '4px'
                            }}>
                              {cashback.userName || "Unknown User"}
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: '#666'
                            }}>
                              ID: {cashback.userId || "N/A"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '20px', whiteSpace: 'nowrap' }}>
                        <div style={{
                          fontSize: '18px',
                          fontWeight: 'bold',
                          color: '#333'
                        }}>
                          {formatAmount(cashback.amountRequested)}
                        </div>
                      </td>
                      <td style={{ padding: '20px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <div style={{
                            padding: '8px',
                            borderRadius: '8px',
                            background: 'var(--bg-tertiary, #f5f5f5)',
                            marginRight: '12px'
                          }}>
                            <FaCreditCard style={{ fontSize: '16px', color: '#666' }} />
                          </div>
                          <span style={{
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#333'
                          }}>
                            {cashback.paymentMethod || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '20px', whiteSpace: 'nowrap' }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '6px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          border: '1px solid',
                          ...(cashback.status === "requested" ? {
                            background: '#fff3e0',
                            color: '#f57c00',
                            borderColor: '#ffcc02'
                          } : cashback.status === "paid" ? {
                            background: '#e8f5e8',
                            color: '#388e3c',
                            borderColor: '#4caf50'
                          } : {
                            background: '#ffebee',
                            color: '#d32f2f',
                            borderColor: '#f44336'
                          })
                        }}>
                          {getStatusIcon(cashback.status)}
                          <span style={{ marginLeft: '6px', textTransform: 'capitalize' }}>
                            {cashback.status}
                          </span>
                        </span>
                      </td>
                      <td style={{ padding: '20px', whiteSpace: 'nowrap' }}>
                        <div style={{ fontSize: '14px', color: '#333' }}>
                          {formatDate(cashback.date)}
                        </div>
                      </td>
                      <td style={{ padding: '20px', whiteSpace: 'nowrap' }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#333'
                        }}>
                          {cashback.monthKey || "N/A"}
                        </div>
                      </td>
                      <td style={{ padding: '20px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button
                            onClick={() => {
                              setSelectedCashback(cashback);
                              setShowModal(true);
                            }}
                            style={{
                              padding: '8px',
                              color: '#2196f3',
                              background: 'transparent',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer'
                            }}
                            title="View Details"
                          >
                            <FaEye style={{ fontSize: '16px' }} />
                          </button>
                          {cashback.status === "requested" && (
                            <>
                              <button
                                onClick={() => updateCashbackStatus(cashback.id, "paid")}
                                style={{
                                  padding: '8px',
                                  color: '#4caf50',
                                  background: 'transparent',
                                  border: 'none',
                                  borderRadius: '8px',
                                  cursor: 'pointer'
                                }}
                                title="Mark as Paid"
                              >
                                <FaCheck style={{ fontSize: '16px' }} />
                              </button>
                              <button
                                onClick={() => updateCashbackStatus(cashback.id, "rejected")}
                                style={{
                                  padding: '8px',
                                  color: '#f44336',
                                  background: 'transparent',
                                  border: 'none',
                                  borderRadius: '8px',
                                  cursor: 'pointer'
                                }}
                                title="Reject"
                              >
                                <FaTimes style={{ fontSize: '16px' }} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {cashbacks.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: 'var(--bg-tertiary, #f5f5f5)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 25px auto',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
              <FaMoneyBillWave style={{ fontSize: '32px', color: '#999' }} />
            </div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: 'var(--text-primary, #333)',
              marginBottom: '10px'
            }}>
              No cashback requests yet
            </h3>
            <p style={{
              color: 'var(--text-secondary, #666)',
              marginBottom: '25px',
              maxWidth: '400px',
              margin: '0 auto 25px auto'
            }}>
            </p>
          </div>
        )}

        {cashbacks.length > 0 && filteredAndSortedCashbacks.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: 'var(--bg-tertiary, #f5f5f5)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 25px auto',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
              <FaSearch style={{ fontSize: '32px', color: '#999' }} />
            </div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: 'var(--text-primary, #333)',
              marginBottom: '10px'
            }}>
              No results found
            </h3>
            <p style={{
              color: 'var(--text-secondary, #666)',
              marginBottom: '25px',
              maxWidth: '400px',
              margin: '0 auto 25px auto'
            }}>
              {filter === "all"
                ? "No cashback requests match your search criteria."
                : `No ${filter} cashback requests found.`
              }
            </p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                style={{
                  padding: '12px 24px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Clear Search
              </button>
            )}
          </div>
        )}

        {showModal && selectedCashback && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            overflowY: 'auto',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}>
            <div style={{
              position: 'relative',
              background: 'var(--bg-card, #ffffff)',
              borderRadius: '15px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              width: '100%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              {/* Modal Header */}
              <div style={{
                position: 'sticky',
                top: 0,
                background: '#3b82f6',
                color: 'white',
                padding: '25px',
                borderRadius: '15px 15px 0 0'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div>
                    <h3 style={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                      margin: '0 0 5px 0'
                    }}>
                      Cashback Details
                    </h3>
                    <p style={{
                      color: 'rgba(255,255,255,0.8)',
                      margin: 0,
                      fontSize: '14px'
                    }}>
                      Complete information about this request
                    </p>
                  </div>
                  <button
                    onClick={() => setShowModal(false)}
                    style={{
                      padding: '8px',
                      background: 'rgba(255,255,255,0.2)',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = 'rgba(255,255,255,0.3)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = 'rgba(255,255,255,0.2)';
                    }}
                  >
                    <FaTimes style={{ fontSize: '20px' }} />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div style={{ padding: '25px' }}>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* User Information */}
                  <div style={{
                    background: '#e3f2fd',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #90caf9'
                  }}>
                    <h4 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: 'var(--text-primary, #333)',
                      marginBottom: '15px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <FaUser style={{ fontSize: '18px', marginRight: '8px', color: '#2196f3' }} />
                      User Information
                    </h4>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr',
                      gap: '15px'
                    }}>
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: 'var(--text-secondary, #666)',
                          marginBottom: '5px'
                        }}>
                          User Name
                        </label>
                        <p style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: 'var(--text-primary, #333)',
                          margin: 0
                        }}>
                          {selectedCashback.userName || "Unknown User"}
                        </p>
                      </div>
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: 'var(--text-secondary, #666)',
                          marginBottom: '5px'
                        }}>
                          User ID
                        </label>
                        <p style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: 'var(--text-primary, #333)',
                          margin: 0
                        }}>
                          {selectedCashback.userId || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Transaction Details */}
                  <div style={{
                    background: '#e8f5e8',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #81c784'
                  }}>
                    <h4 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: 'var(--text-primary, #333)',
                      marginBottom: '15px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <FaMoneyBillWave style={{ fontSize: '18px', marginRight: '8px', color: '#4caf50' }} />
                      Transaction Details
                    </h4>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr',
                      gap: '15px'
                    }}>
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: 'var(--text-secondary, #666)',
                          marginBottom: '5px'
                        }}>
                          Amount Requested
                        </label>
                        <p style={{
                          fontSize: '24px',
                          fontWeight: 'bold',
                          color: '#4caf50',
                          margin: 0
                        }}>
                          {formatAmount(selectedCashback.amountRequested)}
                        </p>
                      </div>
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: 'var(--text-secondary, #666)',
                          marginBottom: '5px'
                        }}>
                          Status
                        </label>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '6px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          border: '1px solid',
                          ...(selectedCashback.status === "requested" ? {
                            background: '#fff3e0',
                            color: '#f57c00',
                            borderColor: '#ffcc02'
                          } : selectedCashback.status === "paid" ? {
                            background: '#e8f5e8',
                            color: '#388e3c',
                            borderColor: '#4caf50'
                          } : {
                            background: '#ffebee',
                            color: '#d32f2f',
                            borderColor: '#f44336'
                          })
                        }}>
                          {getStatusIcon(selectedCashback.status)}
                          <span style={{ marginLeft: '6px', textTransform: 'capitalize' }}>
                            {selectedCashback.status}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Payment Information */}
                  <div style={{
                    background: '#f3e5f5',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #ba68c8'
                  }}>
                    <h4 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: 'var(--text-primary, #333)',
                      marginBottom: '15px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <FaCreditCard style={{ fontSize: '18px', marginRight: '8px', color: '#9c27b0' }} />
                      Payment Information
                    </h4>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr',
                      gap: '15px'
                    }}>
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: 'var(--text-secondary, #666)',
                          marginBottom: '5px'
                        }}>
                          Payment Method
                        </label>
                        <p style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: 'var(--text-primary, #333)',
                          margin: 0
                        }}>
                          {selectedCashback.paymentMethod || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: 'var(--text-secondary, #666)',
                          marginBottom: '5px'
                        }}>
                          Month
                        </label>
                        <p style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: 'var(--text-primary, #333)',
                          margin: 0
                        }}>
                          {selectedCashback.monthKey || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div style={{ marginTop: '15px' }}>
                      <label style={{
                        display: 'block',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: 'var(--text-secondary, #666)',
                        marginBottom: '5px'
                      }}>
                        Date Requested
                      </label>
                      <p style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: 'var(--text-primary, #333)',
                        margin: 0
                      }}>
                        {formatDate(selectedCashback.date)}
                      </p>
                    </div>
                  </div>

                  {/* Payment Details */}
                  {selectedCashback.paymentDetails && (
                    <div style={{
                      background: '#fff3e0',
                      padding: '20px',
                      borderRadius: '12px',
                      border: '1px solid #ffcc02'
                    }}>
                      <h4 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: 'var(--text-primary, #333)',
                        marginBottom: '15px',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <FaCreditCard style={{ fontSize: '18px', marginRight: '8px', color: '#ff9800' }} />
                        Payment Details
                      </h4>
                      <div style={{
                        background: 'var(--bg-card, #ffffff)',
                        padding: '15px',
                        borderRadius: '8px',
                        border: '1px solid #ffcc02'
                      }}>
                        <pre style={{
                          fontSize: '12px',
                          color: 'var(--text-primary, #333)',
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'monospace',
                          margin: 0
                        }}>
                          {typeof selectedCashback.paymentDetails === 'string'
                            ? selectedCashback.paymentDetails
                            : JSON.stringify(selectedCashback.paymentDetails, null, 2)
                          }
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  {selectedCashback.status === "requested" && (
                    <div style={{
                      background: 'var(--bg-tertiary, #f5f5f5)',
                      padding: '20px',
                      borderRadius: '12px',
                      border: '1px solid #bdbdbd'
                    }}>
                      <h4 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: 'var(--text-primary, #333)',
                        marginBottom: '15px'
                      }}>
                        Actions
                      </h4>
                      <div style={{
                        display: 'flex',
                        flexDirection: window.innerWidth < 768 ? 'column' : 'row',
                        gap: '12px'
                      }}>
                        <button
                          onClick={() => updateCashbackStatus(selectedCashback.id, "paid")}
                          style={{
                            flex: 1,
                            background: '#4caf50',
                            color: 'white',
                            padding: '12px 20px',
                            borderRadius: '10px',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <FaCheck style={{ fontSize: '16px', marginRight: '8px' }} />
                          Mark as Paid
                        </button>
                        <button
                          onClick={() => updateCashbackStatus(selectedCashback.id, "rejected")}
                          style={{
                            flex: 1,
                            background: '#f44336',
                            color: 'white',
                            padding: '12px 20px',
                            borderRadius: '10px',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <FaTimes style={{ fontSize: '16px', marginRight: '8px' }} />
                          Reject Request
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Cashbacks;
